import { JuniferCommParser } from "@/models/juniferCommunication/juniferComm.parser"
import { IJuniferComm } from "@/models/juniferCommunication/juniferComm.types"
import { IJuniferTicket } from "@/models/juniferTickets/juniferTicket.types"
import {
  JuniferTicketParser,
  IJuniferTicketRaw,
} from "@/models/juniferTickets/juniferTicket.parser"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  AccountCommunication,
  AccountCommunicationEmail,
} from "@/api/backofficeRest/generated/model"
import { JuniferCommEmailParser } from "@/models/juniferCommunication/juniferCommEmail.parser"
import { IJuniferCommEmail } from "@/models/juniferCommunication/juniferCommEmail.types"

export interface IJuniferService {
  getCommsForAccount(
    billingAccountId: number | null,
  ): QueryResult<IJuniferComm[]>
  getTicketsForAccount(
    billingAccountId: number | null,
  ): QueryResult<IJuniferTicket[]>
  getCommunicationEmail(
    billingAccountId: number | null,
    communicationId: number,
  ): QueryResult<IJuniferCommEmail | null>
  getCommunicationFile(
    billingAccountId: number | null,
    communicationId: number,
    fileId: number,
  ): QueryResult<Blob | null>
}

class JuniferServiceClass implements IJuniferService {
  getCommsForAccount(
    billingAccountId: number | null,
  ): QueryResult<IJuniferComm[]> {
    return useQuery<AccountCommunication[], IJuniferComm[]>({
      ...BackofficeRestQueries.account.juniferComms(billingAccountId ?? 0),
      enabled: !!billingAccountId,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        (data ?? []).map((rawComm) => JuniferCommParser.toDomain(rawComm)),
    })
  }
  getTicketsForAccount(
    billingAccountId: number | null,
  ): QueryResult<IJuniferTicket[]> {
    return useQuery<IJuniferTicketRaw[], IJuniferTicket[]>({
      ...BackofficeRestQueries.account.juniferTickets(billingAccountId ?? 0),
      enabled: !!billingAccountId,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) =>
        (data ?? []).map((item) => JuniferTicketParser.toDomain(item)),
    })
  }
  getCommunicationEmail(
    billingAccountId: number | null,
    communicationId: number,
  ): QueryResult<IJuniferCommEmail | null> {
    return useQuery<AccountCommunicationEmail | void, IJuniferCommEmail | null>(
      {
        ...BackofficeRestQueries.account.juniferCommEmail(
          billingAccountId ?? 0,
          communicationId,
        ),
        enabled: !!billingAccountId,
        placeholderData: null,
        useRawResponse: true,
        // A letter-only communication answers 204, which arrives empty.
        parserFn: (data) =>
          data ? JuniferCommEmailParser.toDomain(data) : null,
      },
    )
  }
  getCommunicationFile(
    billingAccountId: number | null,
    communicationId: number,
    fileId: number,
  ): QueryResult<Blob | null> {
    return useQuery<Blob, Blob | null>({
      ...BackofficeRestQueries.account.juniferCommFile(
        billingAccountId ?? 0,
        communicationId,
        fileId,
      ),
      // Fetched only when the agent asks, via refetch().
      enabled: false,
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data) => data,
    })
  }
}

export const JuniferService = new JuniferServiceClass()
